/**
 * src/lib/liveData
 *
 * 🚀宇宙開発(現在)モジュール専用の実データフェッチ層。
 *
 * engines/ とは性質が異なる：ここにあるのは純粋関数ではなく、外部APIへの
 * 非同期リクエストである。ネットワーク障害・レート制限・APIの仕様変更などで
 * いつでも失敗しうるため、すべての関数は失敗時に null を返し、呼び出し側で
 * 「データを取得できませんでした」という表示にフォールバックできるようにする。
 *
 * 使用している外部API（すべてブラウザから直接fetchできるHTTPS + CORS対応）:
 *  - ISS現在位置:   https://api.wheretheiss.at/v1/satellites/25544
 *  - 今後の打ち上げ: https://ll.thespacedevs.com/2.2.0/launch/upcoming/ (Launch Library 2, TheSpaceDevs)
 *  - 今日の天体写真: https://api.nasa.gov/planetary/apod (NASA APOD, 個人用APIキー使用)
 *
 * 【注意】Open Notify API(人類の現在の宇宙飛行士一覧)はHTTPSに対応していないため、
 * HTTPS配信のこのサイトからは使えない(ブラウザのMixed Content制限で弾かれる)。
 * そのため今回は意図的に採用していない。
 */

/**
 * 一時的な通信エラー(タイムアウト・NASAサーバー側の一過性の不調など)から
 * 自動回復できるよう、失敗時に1回だけ短い間隔を空けてリトライする。
 * 2回目も失敗した場合は、失敗理由をコンソールに記録したうえでnullを返し、
 * 呼び出し側で「データを取得できませんでした」にフォールバックする。
 */
async function fetchWithRetry(label: string, doFetch: () => Promise<Response>): Promise<Response | null> {
  for (let attempt = 1; attempt <= 2; attempt++) {
    try {
      const res = await doFetch();
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res;
    } catch (err) {
      console.error(`[liveData] ${label} 取得失敗 (${attempt}回目):`, err);
      if (attempt === 1) await new Promise((r) => setTimeout(r, 1200));
    }
  }
  return null;
}

export interface ISSPosition {
  latitude: number;
  longitude: number;
  altitudeKm: number;
  velocityKmh: number;
  visibility: string;
  timestampUnix: number;
}

export async function fetchISSPosition(): Promise<ISSPosition | null> {
  const res = await fetchWithRetry('ISS位置', () => fetch('https://api.wheretheiss.at/v1/satellites/25544'));
  if (!res) return null;
  try {
    const d = await res.json();
    return {
      latitude: d.latitude,
      longitude: d.longitude,
      altitudeKm: d.altitude,
      velocityKmh: d.velocity,
      visibility: d.visibility,
      timestampUnix: d.timestamp,
    };
  } catch (err) {
    console.error('[liveData] ISS位置 のJSON解析に失敗:', err);
    return null;
  }
}

export interface LaunchInfo {
  id: string;
  name: string;
  netIso: string; // ISO8601 打ち上げ予定時刻
  providerName: string;
  statusName: string;
  padLocation: string;
  imageUrl: string | null;
}

export async function fetchUpcomingLaunches(limit = 6): Promise<LaunchInfo[] | null> {
  const res = await fetchWithRetry('今後の打ち上げ', () => fetch(`https://ll.thespacedevs.com/2.2.0/launch/upcoming/?limit=${limit}`));
  if (!res) return null;
  try {
    const d = await res.json();
    const results = Array.isArray(d.results) ? d.results : [];
    return results.map(
      (r: any): LaunchInfo => ({
        id: r.id,
        name: r.name ?? 'Unknown',
        netIso: r.net ?? '',
        providerName: r.launch_service_provider?.name ?? 'Unknown',
        statusName: r.status?.name ?? 'TBD',
        padLocation: r.pad?.location?.name ?? '',
        imageUrl: r.image ?? null,
      }),
    );
  } catch (err) {
    console.error('[liveData] 今後の打ち上げ のJSON解析に失敗:', err);
    return null;
  }
}

export interface ApodInfo {
  title: string;
  date: string;
  url: string;
  mediaType: string;
}

const NASA_API_KEY = 'lmBAoHfiCNYhMo8YBPATbN4MNuLGNLU898UPeiee';

export async function fetchApod(): Promise<ApodInfo | null> {
  const res = await fetchWithRetry('今日の天体写真', () => fetch(`https://api.nasa.gov/planetary/apod?api_key=${NASA_API_KEY}`));
  if (res) {
    try {
      const d = await res.json();
      return {
        title: d.title ?? '',
        date: d.date ?? '',
        url: d.media_type === 'image' ? (d.url ?? '') : (d.thumbnail_url ?? d.url ?? ''),
        mediaType: d.media_type ?? 'image',
      };
    } catch (err) {
      console.error('[liveData] 今日の天体写真 のJSON解析に失敗:', err);
    }
  }

  // 【既知の問題】NASA APOD APIは、日付を指定せず「今日」をリクエストすると
  // まれに500エラーを返すことが報告されている(nasa/apod-api issue #155)。
  // 過去の日付を明示的に指定すれば問題なく動くとのことなので、今日の分が
  // 取得できなかった場合は昨日の日付を明示的に指定して再取得を試みる。
  // 表示上は取得できた日付がそのまま画面に出るので、実際には前日の写真が
  // 表示されていても利用者から見て不自然にはならない。
  console.warn('[liveData] 今日の天体写真: 今日分の取得に失敗。昨日の日付で再試行します。');
  const yesterday = new Date(Date.now() - 24 * 3600 * 1000).toISOString().slice(0, 10);
  const fallbackRes = await fetchWithRetry('今日の天体写真(前日フォールバック)', () =>
    fetch(`https://api.nasa.gov/planetary/apod?api_key=${NASA_API_KEY}&date=${yesterday}`),
  );
  if (!fallbackRes) return null;
  try {
    const d = await fallbackRes.json();
    return {
      title: d.title ?? '',
      date: d.date ?? '',
      url: d.media_type === 'image' ? (d.url ?? '') : (d.thumbnail_url ?? d.url ?? ''),
      mediaType: d.media_type ?? 'image',
    };
  } catch (err) {
    console.error('[liveData] 今日の天体写真(前日フォールバック) のJSON解析に失敗:', err);
    return null;
  }
}
